import React, { useEffect, useState } from "react";
import axiosInstance from "../config/axiosInstance";
import API from "../config/config";
import { toast } from "react-toastify";
import { FaSpinner, FaTimes } from "react-icons/fa";

const DriverContactCallStatusModal = ({ driverId, onClose, onSaved }) => {
  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [callStatusForm, setCallStatusForm] = useState({
    contactSource: "",
    language: "",
    assignee: "",
    reporter: "",
    date: "",
    type: "",
    status: "",
    notes: "",
    customStatusValue: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [callStatusHistory, setCallStatusHistory] = useState([]);

  const canEditSection = () => {
    if (!driver) return false;

   /* if (currentUserRole === "superadmin") return true;
    if (
      currentUserRole === "senior" &&
      ["InProgress", "Revisit", "Pending", "Rejected", "Verified"].includes(driver.status)
    ) {
      return true;
    }
    if (
      currentUserRole === "junior" &&
      ["InProgress", "Revisit", "Pending", "Rejected"].includes(driver.status)
    ) {
      return true;
    }  */
    return true; 
  };

  const fetchDriverDetails = async (silentRefresh = false) => {
    if (!driverId) return;
    if (!silentRefresh) setLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const [driverRes, profileRes] = await Promise.all([
        axiosInstance.get(`${API.ADMIN.DRIVER_DETAIL}/${driverId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axiosInstance.get(API.ADMIN.GET_ADMIN_PROFILE, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      const driverData = driverRes.data.data;
      setDriver(driverData);
      setCurrentUserRole(profileRes.data?.data?.role?.slug || "");

      const latestCallStatus =
        driverData.callStatusHistory && driverData.callStatusHistory.length > 0
          ? driverData.callStatusHistory[driverData.callStatusHistory.length - 1]
          : null;
      let formattedDate = "";
      if (latestCallStatus && latestCallStatus.date) {
        const date = new Date(latestCallStatus.date);
        formattedDate = date.toISOString().split("T")[0];
      }
      setCallStatusForm({
        contactSource: driverData.contactSource || "",
        language: driverData.language || "",
        assignee:
          (typeof driverData.assignee === "string"
            ? driverData.assignee
            : driverData.assignee?.fullName) ||
          driverData.assignedEmployee?.fullName ||
          "",
        reporter: typeof driverData.reporter === "string" ? driverData.reporter : driverData.reporter?.fullName || "",
        date: formattedDate,
        type: latestCallStatus?.type || "",
        status: latestCallStatus?.status || "",
        notes: latestCallStatus?.notes || "",
        customStatusValue: latestCallStatus?.customStatusValue ?? "",
      });
      setCallStatusHistory(driverData.callStatusHistory || []);
    } catch (error) {
      console.error("Error fetching driver details:", error);
      toast.error("Failed to load driver details");
      if (!silentRefresh) onClose();
    } finally {
      if (!silentRefresh) setLoading(false);
    }
  };

  useEffect(() => {
    if (driverId) fetchDriverDetails();
  }, [driverId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCallStatusChange = (field, value) => {
    setCallStatusForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCallStatusSubmit = async (e) => {
    e.preventDefault();
    if (!driverId || !driver) return;
    try {
      setSubmitting(true);
      const token = localStorage.getItem("adminToken");
      const payload = {};
      if (driver?.fullName) payload.fullName = driver.fullName;
      if (driver?.phone) payload.phone = driver.phone;
      if (callStatusForm.contactSource) payload.contactSource = callStatusForm.contactSource;
      if (callStatusForm.language) payload.language = callStatusForm.language;
      if (callStatusForm.assignee?.trim()) payload.assignee = callStatusForm.assignee.trim();
      if (callStatusForm.reporter?.trim()) payload.reporter = callStatusForm.reporter.trim();
      const hasCallStatusData =
        callStatusForm.date ||
        callStatusForm.type ||
        callStatusForm.status ||
        callStatusForm.status === "Custom Filed";
      if (hasCallStatusData) {
        payload.callStatusHistoryEntry = {
          date: callStatusForm.date,
          type: callStatusForm.type,
          status: callStatusForm.status,
          customStatusValue: callStatusForm.customStatusValue || "",
          notes: callStatusForm.notes || "",
        };
      }
      const response = await axiosInstance.patch(
        `${API.ADMIN.UPDATE_DRIVER}/${driverId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success("Driver profile updated successfully!");
        await fetchDriverDetails(true);
        if (onSaved) onSaved();
      } else {
        toast.error(response.data.message || "Failed to update driver profile");
      }
    } catch (error) {
      console.error("Error updating driver profile:", error);
      toast.error(error.response?.data?.message || "Failed to update driver profile");
    } finally {
      setSubmitting(false);
    }
  };

  if (!driverId) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-modal="true">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
        <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
            <h2 className="text-xl font-bold text-gray-900">
              Contact & Call Status {driver && `– ${driver.fullName || driver.phone || "Driver"}`}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              aria-label="Close"
            >
              <FaTimes className="text-lg" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <FaSpinner className="animate-spin text-4xl text-blue-500 mb-4" />
                <p className="text-gray-600">Loading driver details...</p>
              </div>
            ) : (
              <>
                <form className="space-y-6" onSubmit={handleCallStatusSubmit}>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contact Source</label>
                    <select
                      value={callStatusForm.contactSource}
                      onChange={(e) => handleCallStatusChange("contactSource", e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={!canEditSection()}
                    >
                      <option value="">Select Contact Source</option>
                      <option value="WhatsApp Group">WhatsApp Group</option>
                      <option value="Google Ads">Google Ads</option>
                      <option value="Meta Ads">Meta Ads</option>
                      <option value="Google Search Organic">Google Search Organic</option>
                      <option value="Job Posting Paid">Job Posting Paid</option>
                      <option value="Job Posting Organic">Job Posting Organic</option>
                      <option value="Apna Jobs Paid">Apna Jobs Paid</option>
                      <option value="Workindia Paid">Workindia Paid</option>
                      <option value="OLX Paid">OLX Paid</option>
                      <option value="Apna Jobs Organic">Apna Jobs Organic</option>
                      <option value="Workindia Organic">Workindia Organic</option>
                      <option value="OLX Organic">OLX Organic</option>
                      <option value="Custom">Custom</option>
                    </select>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Call Status</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                        <input
                          type="date"
                          value={callStatusForm.date}
                          onChange={(e) => handleCallStatusChange("date", e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          disabled={!canEditSection()}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                        <select
                          value={callStatusForm.type}
                          onChange={(e) => handleCallStatusChange("type", e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          disabled={!canEditSection()}
                        >
                          <option value="">Select Type</option>
                          <option value="Initial Call">Initial Call</option>
                          <option value="1st Followup">1st Followup</option>
                          <option value="2nd Followup">2nd Followup</option>
                          <option value="3rd Followup">3rd Followup</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                        <select
                          value={callStatusForm.status}
                          onChange={(e) => handleCallStatusChange("status", e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          disabled={!canEditSection()}
                        >
                          <option value="">Select Status</option>
                          <option value="Not Answered">Not Answered</option>
                          <option value="Number Not Valid">Number Not Valid</option>
                          <option value="Agreed to Fill the Form">Agreed to Fill the Form</option>
                          <option value="Agreed to Send Documents">Agreed to Send Documents</option>
                          <option value="Not Interested">Not Interested</option>
                          <option value="Message Sent">Message Sent</option>
                          <option value="Docs Received">Docs Received</option>
                          <option value="Docs Uploaded">Docs Uploaded</option>
                          <option value="Custom Filed">Custom Filed</option>
                        </select>
                      </div>
                    </div>
                    {callStatusForm.status === "Custom Filed" && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Custom status value</label>
                        <input
                          type="text"
                          value={callStatusForm.customStatusValue}
                          onChange={(e) => handleCallStatusChange("customStatusValue", e.target.value)}
                          placeholder="Enter custom status..."
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          disabled={!canEditSection()}
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                      <textarea
                        value={callStatusForm.notes}
                        onChange={(e) => handleCallStatusChange("notes", e.target.value)}
                        placeholder="Enter any additional notes..."
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={!canEditSection()}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
                    <select
                      value={callStatusForm.language}
                      onChange={(e) => handleCallStatusChange("language", e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={!canEditSection()}
                    >
                      <option value="">Select Language</option>
                      <option value="Hindi">Hindi</option>
                      <option value="Telugu">Telugu</option>
                      <option value="Tamil">Tamil</option>
                      <option value="Kannada">Kannada</option>
                      <option value="Marathi">Marathi</option>
                      <option value="Gujarati">Gujarati</option>
                      <option value="Custom Field">Custom Field</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Assignee</label>
                      <input
                        type="text"
                        value={callStatusForm.assignee}
                        onChange={(e) => handleCallStatusChange("assignee", e.target.value)}
                        placeholder="Enter assignee name"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={!canEditSection()}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Reporter</label>
                      <input
                        type="text"
                        value={callStatusForm.reporter}
                        onChange={(e) => handleCallStatusChange("reporter", e.target.value)}
                        placeholder="Enter reporter name"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={!canEditSection()}
                      />
                    </div>
                  </div>

                  {canEditSection() && (
                    <div className="flex justify-end pt-4">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                      >
                        {submitting ? (
                          <>
                            <FaSpinner className="animate-spin mr-2" />
                            Saving...
                          </>
                        ) : (
                          "Save Call Status"
                        )}
                      </button>
                    </div>
                  )}
                </form>

                {callStatusHistory && callStatusHistory.length > 0 && (
                  <div className="mt-8">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Call Status History</h4>
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Created At</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {callStatusHistory
                              .slice()
                              .reverse()
                              .map((entry, index) => {
                                const entryDate = new Date(entry.date);
                                const createdAt = new Date(entry.createdAt);
                                return (
                                  <tr key={entry._id || index} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                      {entryDate.toLocaleDateString("en-GB", {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                      })}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{entry.type}</td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                        {entry.status === "Custom Filed"
                                          ? `Custom Filed - ${entry.customStatusValue?.trim() || ""}`.replace(/ - $/, "")
                                          : entry.status}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-500">{entry.notes || "-"}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                      {createdAt.toLocaleString("en-GB", {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverContactCallStatusModal;
